import { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FaCircleCheck, FaSpinner, FaTriangleExclamation, FaXmark } from "react-icons/fa6";
import {
    AgentAction,
    FeedbackRating,
    SourceLink,
    clearKnowledgeSession,
    sendChatMessage,
    sendMessageFeedback,
    uploadKnowledgeDocument,
} from "../../api/documentAgent";
import { toastifyConfigs } from "../../config/constants";
import BoxAnimation from "../animation/BoxAnimation";
import Header from "../theme-one/Header";

type ChatRole = "assistant" | "user";

type ChatMessage = {
    id: string;
    role: ChatRole;
    text: string;
    createdAt: Date;
    messageId?: string;
    sources?: SourceLink[];
    actions?: AgentAction[];
    escalationRecommended?: boolean;
    feedback?: FeedbackRating;
};

type ConversationSession = {
    conversationId: string;
    createdAt: number;
};

// Discriminated union so each upload can only ever be in one well-formed
// state — no "isIndexed && isFailed" combinations to guard against.
type UploadedDocumentEntry =
    | { id: string; fileName: string; status: "uploading" }
    | { id: string; fileName: string; status: "indexed"; chunkCount: number | null }
    | { id: string; fileName: string; status: "failed"; errorMessage: string };

const CONVERSATION_SESSION_STORAGE_KEY = "qouda.documentChat.conversationSession";
const CONVERSATION_SESSION_TTL_MS = 2 * 60 * 60 * 1000;
const MAX_DOCUMENT_FILE_BYTES = 50_000_000;
const DOCUMENT_EXTENSIONS = [
    ".pdf", ".docx", ".xlsx", ".pptx",
    ".txt", ".md", ".csv", ".tsv", ".json", ".xml",
    ".html", ".htm", ".rtf", ".odt", ".ods", ".odp", ".epub",
];
const DOCUMENT_FILE_ACCEPT = DOCUMENT_EXTENSIONS.join(",");

// react-toastify config for error toasts: everything else uses the shared
// default (auto-closes after 5s), but errors stay up until the user
// dismisses them so a failure can't silently disappear.
const errorToastConfig = { ...toastifyConfigs, autoClose: false };

function createGuid() {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
        return crypto.randomUUID();
    }

    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
        const randomValue = Math.random() * 16 | 0;
        const value = char === "x" ? randomValue : (randomValue & 0x3) | 0x8;
        return value.toString(16);
    });
}

function createWelcomeMessage(): ChatMessage {
    return {
        id: createGuid(),
        role: "assistant",
        text: "Hi! I’m Qouda’s assistant. Upload a document and ask me anything about the information in this chat.",
        createdAt: new Date(),
    };
}

function createSessionExpiredMessage(): ChatMessage {
    return {
        id: createGuid(),
        role: "assistant",
        text: "Your session expired after two hours. I’ve started a fresh session — please re-upload any documents you’d like me to reference before asking again.",
        createdAt: new Date(),
    };
}

// Shown instead of createSessionExpiredMessage() when expiry is discovered at
// send time, so the user knows their typed message was never sent (rather
// than silently vanishing) and that it's still sitting in the textbox.
function createSessionExpiredBeforeSendMessage(): ChatMessage {
    return {
        id: createGuid(),
        role: "assistant",
        text: "Your session expired and this message was not sent — your documents need to be re-uploaded. Your message is still in the box below.",
        createdAt: new Date(),
    };
}

const STALE_UPLOAD_MESSAGE = "The session expired before this document was ready — please upload it again.";

function getErrorMessage(error: unknown, fallback: string) {
    if (error instanceof TypeError) {
        return "The chat service could not be reached. Please try again shortly.";
    }

    if (error instanceof Error && error.message) {
        return error.message;
    }

    return fallback;
}

function persistConversationSession(session: ConversationSession) {
    try {
        window.sessionStorage.setItem(CONVERSATION_SESSION_STORAGE_KEY, JSON.stringify(session));
    } catch {
        // The in-memory state still works when storage is unavailable.
    }
}

function createConversationSession(conversationId = createGuid()): ConversationSession {
    const session = { conversationId, createdAt: Date.now() };
    if (typeof window !== "undefined") persistConversationSession(session);
    return session;
}

function getInitialConversationSession(): ConversationSession {
    if (typeof window === "undefined") return createConversationSession();

    try {
        const storedSession = window.sessionStorage.getItem(CONVERSATION_SESSION_STORAGE_KEY);
        if (storedSession) {
            const parsed = JSON.parse(storedSession) as Partial<ConversationSession>;
            const createdAt = Number(parsed.createdAt);
            const conversationId = typeof parsed.conversationId === "string" ? parsed.conversationId : "";
            const isFresh = Number.isFinite(createdAt) && Date.now() - createdAt < CONVERSATION_SESSION_TTL_MS;
            if (conversationId && isFresh) return { conversationId, createdAt };
        }
    } catch {
        window.sessionStorage.removeItem(CONVERSATION_SESSION_STORAGE_KEY);
    }

    return createConversationSession();
}

// Single source of truth for "has this session timed out" — both the
// background timer and the send-message guard call this instead of each
// keeping their own copy of the TTL math.
function isSessionExpired(session: ConversationSession) {
    return Date.now() - session.createdAt >= CONVERSATION_SESSION_TTL_MS;
}

export default function DocumentChatPage() {
    const navigate = useNavigate();
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const messageListRef = useRef<HTMLDivElement | null>(null);
    // Bumped every time the session is reset (expiry only — a manual "Clear
    // chat" already can't race in-flight work because it disables the upload
    // input and send button while it's pending). In-flight requests capture
    // this value before their await and compare it after; a mismatch means
    // the session they started under is dead and their result must not be
    // applied to the UI. A ref (not state) so the check is synchronous and
    // doesn't wait for a re-render.
    const sessionEpochRef = useRef(0);
    const [conversationSession, setConversationSession] = useState<ConversationSession>(() => getInitialConversationSession());
    const [messages, setMessages] = useState<ChatMessage[]>(() => [createWelcomeMessage()]);
    const [draftMessage, setDraftMessage] = useState("");
    const [isSending, setIsSending] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isClearing, setIsClearing] = useState(false);
    const [uploadedDocuments, setUploadedDocuments] = useState<UploadedDocumentEntry[]>([]);

    const conversationId = conversationSession.conversationId;
    const activeUploadFileName = uploadedDocuments.find((entry) => entry.status === "uploading")?.fileName;
    const hasFailedUploads = uploadedDocuments.some((entry) => entry.status === "failed");

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }, []);

    useEffect(() => {
        const messageList = messageListRef.current;
        // Guard for environments without Element.scrollTo (e.g. jsdom in tests,
        // some embedded webviews) so the effect can't throw and break rendering.
        if (messageList && typeof messageList.scrollTo === "function") {
            messageList.scrollTo({ top: messageList.scrollHeight, behavior: "smooth" });
        }
    }, [messages, isSending]);

    // Resets to a brand-new session: fresh conversationId, a visible notice
    // in the transcript, and the uploaded-documents panel cleared, since any
    // previously uploaded documents no longer belong to the active session.
    // The draft is deliberately left alone — a background expiry must not
    // erase a message the user is mid-composing, and the send-guard path
    // (which passes its own notice) explicitly relies on the draft surviving
    // so the user can retry after re-uploading.
    const expireSession = useCallback((noticeMessage: ChatMessage = createSessionExpiredMessage()) => {
        sessionEpochRef.current += 1;
        setConversationSession(createConversationSession());
        setMessages([noticeMessage]);
        setUploadedDocuments([]);
    }, []);

    useEffect(() => {
        const expiresIn = Math.max(0, CONVERSATION_SESSION_TTL_MS - (Date.now() - conversationSession.createdAt));
        const timer = window.setTimeout(expireSession, expiresIn);

        return () => window.clearTimeout(timer);
    }, [conversationSession, expireSession]);

    const updateUploadEntry = (id: string, entry: UploadedDocumentEntry) => {
        setUploadedDocuments((current) => current.map((item) => (item.id === id ? entry : item)));
    };

    const handleDismissUpload = (id: string) => {
        setUploadedDocuments((current) => current.filter((item) => item.id !== id));
    };

    const handleClearFailedUploads = () => {
        setUploadedDocuments((current) => current.filter((item) => item.status !== "failed"));
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files || []);
        if (!files.length) return;

        // Snapshot which session this batch belongs to. The files upload
        // sequentially, and the 2-hour expiry timer can fire between any two
        // of them (or while one is awaiting the network) — every file that
        // started after that point must not be sent under, or credited to, a
        // conversationId the backend has already discarded.
        const batchEpoch = sessionEpochRef.current;

        try {
            setIsUploading(true);

            for (const file of files) {
                const entryId = createGuid();

                if (sessionEpochRef.current !== batchEpoch) {
                    // The session expired before this file's turn came up — never
                    // send it under the dead conversationId.
                    setUploadedDocuments((current) => [...current, { id: entryId, fileName: file.name, status: "failed", errorMessage: STALE_UPLOAD_MESSAGE }]);
                    continue;
                }

                setUploadedDocuments((current) => [...current, { id: entryId, fileName: file.name, status: "uploading" }]);

                const extension = file.name.includes(".") ? `.${file.name.split(".").pop()!.toLowerCase()}` : "";
                if (!DOCUMENT_EXTENSIONS.includes(extension) || file.size > MAX_DOCUMENT_FILE_BYTES) {
                    const message = file.size > MAX_DOCUMENT_FILE_BYTES
                        ? "The file is larger than the 50 MB limit."
                        : "This file type is not supported.";
                    updateUploadEntry(entryId, { id: entryId, fileName: file.name, status: "failed", errorMessage: message });
                    toast.error(`${file.name}: ${message}`, errorToastConfig as any);
                    continue;
                }

                try {
                    const result = await uploadKnowledgeDocument(file, conversationId);

                    if (sessionEpochRef.current !== batchEpoch) {
                        // Expired while this upload was in flight. The backend may
                        // have indexed it, but it belongs to a conversation the
                        // client has already torn down — surface it as failed (if
                        // its entry is even still present) and never fire the
                        // "ready for questions" success toast for a dead session.
                        updateUploadEntry(entryId, { id: entryId, fileName: file.name, status: "failed", errorMessage: STALE_UPLOAD_MESSAGE });
                        continue;
                    }

                    // Treat a 200 where indexed isn't exactly true as a failure too —
                    // guards against an older deployed backend that returns success
                    // with a partial/failed indexing state instead of an error status.
                    if (result.indexed === true) {
                        updateUploadEntry(entryId, {
                            id: entryId,
                            fileName: file.name,
                            status: "indexed",
                            chunkCount: typeof result.chunkCount === "number" ? result.chunkCount : null,
                        });
                        toast.success(`${file.name} is ready for questions.`, toastifyConfigs as any);
                    } else {
                        const message = result.indexingError || "The document could not be indexed.";
                        updateUploadEntry(entryId, { id: entryId, fileName: file.name, status: "failed", errorMessage: message });
                        toast.error(`${file.name}: ${message}`, errorToastConfig as any);
                    }
                } catch (error) {
                    if (sessionEpochRef.current !== batchEpoch) {
                        updateUploadEntry(entryId, { id: entryId, fileName: file.name, status: "failed", errorMessage: STALE_UPLOAD_MESSAGE });
                        continue;
                    }

                    const message = getErrorMessage(error, "Upload failed.");
                    updateUploadEntry(entryId, { id: entryId, fileName: file.name, status: "failed", errorMessage: message });
                    toast.error(`${file.name}: ${message}`, errorToastConfig as any);
                }
            }

        } finally {
            setIsUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const handleSendMessage = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const messageText = draftMessage.trim();
        if (!messageText || isSending) return;

        if (isSessionExpired(conversationSession)) {
            // Preserve the draft (expireSession no longer clears it) and show a
            // notice that explicitly says the message was NOT sent, instead of
            // the generic background-expiry notice.
            expireSession(createSessionExpiredBeforeSendMessage());
            return;
        }

        const sendEpoch = sessionEpochRef.current;

        setMessages((current) => [...current, {
            id: createGuid(),
            role: "user",
            text: messageText,
            createdAt: new Date(),
        }]);
        setDraftMessage("");

        try {
            setIsSending(true);
            const result = await sendChatMessage(messageText, conversationId);

            if (sessionEpochRef.current !== sendEpoch) {
                // The session expired while this reply was in flight. The
                // transcript was already reset with its own expiry notice —
                // appending this reply now would attach a dead conversation's
                // answer to a question that's no longer visible. Discard it
                // (and, since the message is never added, its sources/feedback
                // UI never renders either).
                return;
            }

            setMessages((current) => [...current, {
                id: createGuid(),
                role: "assistant",
                text: result.answer,
                createdAt: new Date(),
                messageId: result.messageId,
                sources: result.sources || [],
                actions: result.actions || [],
                escalationRecommended: result.escalationRecommended,
            }]);
        } catch (error) {
            if (sessionEpochRef.current !== sendEpoch) {
                return;
            }

            const message = getErrorMessage(error, "Unable to send the message.");
            setMessages((current) => [...current, {
                id: createGuid(),
                role: "assistant",
                text: message,
                createdAt: new Date(),
            }]);
            toast.error(message, errorToastConfig as any);
        } finally {
            setIsSending(false);
        }
    };

    const handleFeedback = async (localMessageId: string, backendMessageId: string, rating: FeedbackRating) => {
        try {
            await sendMessageFeedback(backendMessageId, conversationId, rating);
            setMessages((current) => current.map((message) =>
                message.id === localMessageId ? { ...message, feedback: rating } : message));
            toast.success("Thanks for the feedback.", toastifyConfigs as any);
        } catch (error) {
            toast.error(getErrorMessage(error, "Unable to save feedback."), errorToastConfig as any);
        }
    };

    const handleClearConversation = async () => {
        try {
            setIsClearing(true);
            const result = await clearKnowledgeSession(conversationId);
            setConversationSession(createConversationSession(result.conversationId));
            toast.success(result.message, toastifyConfigs as any);
        } catch (error) {
            toast.error(getErrorMessage(error, "Unable to clear this chat."), errorToastConfig as any);
            return;
        } finally {
            setIsClearing(false);
        }

        setMessages([createWelcomeMessage()]);
        setDraftMessage("");
        setUploadedDocuments([]);
    };

    return (
        <div className="qouda-home min-h-screen bg-primary_one-150 text-primary_one-50">
            <Header />
            <main className="mx-auto max-w-screen-xl px-4 pb-10 pt-4 md:pb-16 md:pt-8">
                <div className="mb-8 grid items-start gap-6 lg:grid-cols-[380px_1fr]">
                    <BoxAnimation>
                        <aside className="flex h-[calc(100vh-155px)] min-h-[450px] max-h-[720px] flex-col overflow-hidden rounded-2xl border border-primary_one-100 bg-primary_one-140 p-7 shadow-xl">
                            <div className="mb-4 flex-shrink-0">
                                <label className="mb-3 block text-[20px] font-semibold" htmlFor="document-upload">Add knowledge</label>
                                <input
                                    ref={fileInputRef}
                                    id="document-upload"
                                    type="file"
                                    accept={DOCUMENT_FILE_ACCEPT}
                                    multiple
                                    onChange={handleFileChange}
                                    disabled={isUploading || isClearing}
                                    className="block w-full rounded-md border border-primary_one-100 bg-primary_one-150 px-4 py-3 text-sm text-gray-300 file:mr-4 file:rounded-md file:border-0 file:bg-primary_one-200 file:px-4 file:py-2 file:font-semibold file:text-white disabled:cursor-not-allowed disabled:opacity-60"
                                />

                                {activeUploadFileName && (
                                    <div role="status" className="mt-3 flex items-center gap-2 rounded-md border border-primary_one-100 bg-primary_one-150 px-3 py-2 text-xs text-gray-300">
                                        <FaSpinner className="flex-shrink-0 animate-spin text-primary_one-200" aria-hidden="true" />
                                        <span className="truncate">Uploading {activeUploadFileName}…</span>
                                    </div>
                                )}
                            </div>

                            <div className="flex min-h-0 flex-1 flex-col">
                                <div className="mb-2 flex flex-shrink-0 items-center justify-between gap-2">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Uploaded documents</p>
                                    {hasFailedUploads && (
                                        <button
                                            type="button"
                                            onClick={handleClearFailedUploads}
                                            className="text-xs font-semibold text-primary_one-200 hover:underline"
                                        >
                                            Clear failed
                                        </button>
                                    )}
                                </div>

                                {uploadedDocuments.length === 0 ? (
                                    <p className="text-xs text-gray-500">No documents uploaded yet.</p>
                                ) : (
                                    <ul aria-live="polite" className="min-h-0 flex-1 space-y-2 overflow-y-auto pr-1">
                                        {uploadedDocuments.map((entry) => (
                                            <li
                                                key={entry.id}
                                                className={`rounded-md border px-3 py-2 text-xs ${entry.status === "failed"
                                                    ? "border-red-900/60 bg-red-950/20"
                                                    : "border-primary_one-100 bg-primary_one-150"}`}
                                            >
                                                <div className="flex items-center justify-between gap-2">
                                                    <span className="truncate font-medium text-gray-200" title={entry.fileName}>
                                                        {entry.fileName}
                                                    </span>
                                                    <span className="flex flex-shrink-0 items-center gap-1.5">
                                                        {entry.status === "uploading" && (
                                                            <FaSpinner className="animate-spin text-gray-400" aria-hidden="true" title="Uploading" />
                                                        )}
                                                        {entry.status === "indexed" && (
                                                            <FaCircleCheck className="text-emerald-400" aria-hidden="true" title="Indexed" />
                                                        )}
                                                        {entry.status === "failed" && (
                                                            <FaTriangleExclamation className="text-red-400" aria-hidden="true" title="Failed" />
                                                        )}
                                                        {entry.status !== "uploading" && (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleDismissUpload(entry.id)}
                                                                aria-label={`Dismiss ${entry.fileName}`}
                                                                className="rounded p-0.5 text-gray-500 transition-colors hover:text-gray-300"
                                                            >
                                                                <FaXmark aria-hidden="true" />
                                                            </button>
                                                        )}
                                                    </span>
                                                </div>

                                                {entry.status === "indexed" && (
                                                    <p className="mt-1 text-gray-400">
                                                        Indexed{entry.chunkCount != null ? ` · ${entry.chunkCount} chunk${entry.chunkCount === 1 ? "" : "s"}` : ""}
                                                    </p>
                                                )}
                                                {entry.status === "failed" && (
                                                    <p className="mt-1 text-red-300">{entry.errorMessage}</p>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>

                            <button
                                type="button"
                                onClick={handleClearConversation}
                                disabled={isClearing || isSending || isUploading}
                                className="mt-4 w-full flex-shrink-0 rounded-md border border-primary_one-200 px-6 py-3 font-semibold uppercase tracking-wide text-primary_one-200 transition-colors hover:bg-primary_one-200 hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {isClearing ? "Clearing..." : "Clear chat"}
                            </button>
                        </aside>
                    </BoxAnimation>

                    <BoxAnimation>
                        <section className="flex h-[calc(100vh-155px)] min-h-[450px] max-h-[720px] flex-col overflow-hidden rounded-2xl border border-primary_one-100 bg-primary_one-140 p-7 shadow-xl">
                            <div className="mb-5 flex flex-shrink-0 items-center justify-between gap-4 border-b border-primary_one-100 pb-4 max-md:flex-col max-md:items-start">
                                <div>
                                    <h2 className="text-[28px] font-bold max-md:text-[24px]">Chat with Qouda</h2>
                                    <p className="mt-1 text-xs text-gray-400">Conversation session expires after two hours</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => navigate("/")}
                                    className="rounded-md border border-primary_one-200 px-5 py-2 text-sm font-semibold uppercase tracking-wide text-primary_one-200 transition-colors hover:bg-primary_one-200 hover:text-white"
                                >
                                    Back home
                                </button>
                            </div>

                            <div ref={messageListRef} className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-2" aria-live="polite">
                                {messages.map((message) => (
                                    <article
                                        key={message.id}
                                        className={`max-w-[88%] rounded-2xl px-5 py-4 ${message.role === "user"
                                            ? "ml-auto bg-primary_one-200 text-white"
                                            : "mr-auto border border-primary_one-100 bg-primary_one-150 text-gray-200"}`}
                                    >
                                        <p className="mb-2 text-xs font-semibold uppercase tracking-wide opacity-80">
                                            {message.role === "user" ? "You" : "Qouda"}
                                        </p>
                                        <p className="whitespace-pre-wrap leading-7">{message.text}</p>

                                        {!!message.sources?.length && (
                                            <div className="mt-4 border-t border-primary_one-100 pt-3">
                                                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">Sources</p>
                                                <ul className="space-y-1 text-sm">
                                                    {message.sources.map((source, index) => (
                                                                <li key={`${source.title}-${index}`}>
                                                                    {source.url ? (
                                                                        <a href={source.url} target="_blank" rel="noreferrer" className="text-primary_one-200 underline hover:text-red-300">
                                                                            {source.title}{source.pageNumber ? ` — page ${source.pageNumber}` : ""}
                                                                        </a>
                                                                    ) : (
                                                                        <>{source.title}{source.pageNumber ? ` — page ${source.pageNumber}` : ""}</>
                                                                    )}
                                                                </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}

                                        {message.role === "assistant" && message.messageId && (
                                            <div className="mt-4 flex items-center gap-2 border-t border-primary_one-100 pt-3 text-xs">
                                                <span className="text-gray-400">Was this helpful?</span>
                                                <button
                                                    type="button"
                                                    onClick={() => handleFeedback(message.id, message.messageId!, "helpful")}
                                                    disabled={!!message.feedback}
                                                    className="rounded border border-primary_one-100 px-2 py-1 hover:border-primary_one-200 disabled:opacity-60"
                                                >
                                                    {message.feedback === "helpful" ? "Helpful ✓" : "Helpful"}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleFeedback(message.id, message.messageId!, "not_helpful")}
                                                    disabled={!!message.feedback}
                                                    className="rounded border border-primary_one-100 px-2 py-1 hover:border-primary_one-200 disabled:opacity-60"
                                                >
                                                    {message.feedback === "not_helpful" ? "Not helpful ✓" : "Not helpful"}
                                                </button>
                                            </div>
                                        )}
                                    </article>
                                ))}

                                {isSending && (
                                    <article className="mr-auto max-w-[88%] rounded-2xl border border-primary_one-100 bg-primary_one-150 px-5 py-4 text-gray-300">
                                        Qouda is checking your knowledge...
                                    </article>
                                )}
                            </div>

                            <form onSubmit={handleSendMessage} className="mt-6 flex flex-shrink-0 gap-3 max-md:flex-col">
                                <textarea
                                    value={draftMessage}
                                    onChange={(event) => setDraftMessage(event.target.value)}
                                    placeholder="Ask about this chat’s documents..."
                                    maxLength={2000}
                                    disabled={isSending || isClearing || isUploading}
                                    className="min-h-[58px] flex-1 resize-none rounded-md border border-primary_one-100 bg-primary_one-150 px-4 py-3 text-white outline-none transition-colors placeholder-gray-400 focus:border-primary_one-200 disabled:cursor-not-allowed disabled:opacity-60"
                                />
                                <button
                                    type="submit"
                                    disabled={!draftMessage.trim() || isSending || isClearing || isUploading}
                                    className="rounded-md bg-primary_one-200 px-8 py-3 font-semibold uppercase tracking-wide text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    {isSending ? "Sending..." : "Send"}
                                </button>
                            </form>
                        </section>
                    </BoxAnimation>
                </div>
            </main>
        </div>
    );
}
